import { computed, reactive, toRefs } from "vue";

export type PlaybackStatus =
  "idle" | "loading" | "buffering" | "paused" | "playing" | "error";

type HtmlAudioState = {
  status: PlaybackStatus;
  ended: boolean;
  timeElapsed: number | undefined;
  duration: number | undefined;
  buffered: number | undefined;
};

type UseHtmlAudioOptions = {
  signal: AbortSignal;
  transformSourceUrl?: (sourceUrl: string) => string;
  onBeforePlay: () => void;
  onEnded: () => void;
  onError?: (error: Error) => void;
};

type PlaybackIntent = "explicit" | "autoplay";

export function useHtmlAudio({
  signal,
  transformSourceUrl = (sourceUrl) => sourceUrl,
  onBeforePlay,
  onEnded,
  onError,
}: UseHtmlAudioOptions) {
  const state = reactive<HtmlAudioState>({
    status: "idle",
    ended: false,
    timeElapsed: undefined,
    duration: undefined,
    buffered: undefined,
  });
  const isPlaybackRequested = computed(
    () =>
      state.status === "loading" ||
      state.status === "buffering" ||
      state.status === "playing",
  );
  const position = computed(() => {
    const { duration, timeElapsed } = state;

    if (
      timeElapsed === undefined ||
      duration === undefined ||
      !Number.isFinite(timeElapsed) ||
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      return undefined;
    }

    return Math.min(100, Math.max(0, (timeElapsed / duration) * 100));
  });
  const audioElement = new Audio();

  let configuredSourceUrl: string | undefined;

  function toError(cause: unknown, fallbackMessage: string): Error {
    const message =
      (cause instanceof Error || cause instanceof MediaError) && cause.message
        ? cause.message
        : fallbackMessage;

    return new Error(message, { cause });
  }

  function reportError(error: Error) {
    if (state.status === "error") {
      return;
    }

    state.status = "error";
    audioElement.pause();
    onError?.(error);
  }

  function clearSource() {
    configuredSourceUrl = undefined;
    state.ended = false;

    if (audioElement.getAttribute("src") !== null) {
      audioElement.pause();
      audioElement.removeAttribute("src");
      audioElement.removeAttribute("preload");
      audioElement.load();
    }

    state.duration = undefined;
    state.buffered = undefined;
  }

  function getPlayableSourceUrl(sourceUrl: string) {
    try {
      const playableSourceUrl = transformSourceUrl(sourceUrl);

      if (playableSourceUrl) {
        return playableSourceUrl;
      }

      throw new Error("Audio source unavailable");
    } catch (cause) {
      throw toError(cause, "Audio source unavailable");
    }
  }

  function loadSource(sourceUrl: string) {
    const playableSourceUrl = getPlayableSourceUrl(sourceUrl);

    if (configuredSourceUrl === playableSourceUrl) {
      return;
    }

    try {
      clearSource();
      audioElement.setAttribute("preload", "metadata");
      audioElement.setAttribute("src", playableSourceUrl);
      audioElement.load();

      if (state.timeElapsed !== undefined) {
        audioElement.currentTime = state.timeElapsed;
      }

      configuredSourceUrl = playableSourceUrl;
    } catch (cause) {
      configuredSourceUrl = undefined;
      throw toError(cause, "Audio source preparation failed");
    }
  }

  function updateDuration() {
    if (!configuredSourceUrl) {
      return;
    }

    const duration = audioElement.duration;

    if (!Number.isFinite(duration) || duration < 0) {
      return;
    }

    state.duration = duration;

    if (state.timeElapsed !== undefined) {
      seekTo(state.timeElapsed);
    }
  }

  function updateBufferingStatus() {
    if (configuredSourceUrl && isPlaybackRequested.value) {
      state.status = state.duration === undefined ? "loading" : "buffering";
    }
  }

  const eventListeners = {
    loadedmetadata: updateDuration,
    durationchange: updateDuration,
    waiting: updateBufferingStatus,
    stalled: updateBufferingStatus,
    pause: () => {
      if (!configuredSourceUrl || audioElement.ended) {
        return;
      }

      if (state.status !== "error") {
        state.status = "paused";
      }
    },
    playing: () => {
      if (!configuredSourceUrl) {
        return;
      }

      if (!isPlaybackRequested.value) {
        audioElement.pause();

        if (state.status !== "error") {
          state.status = "paused";
        }

        return;
      }

      state.ended = false;
      state.status = "playing";
    },
    timeupdate: () => {
      if (
        !configuredSourceUrl ||
        audioElement.readyState === audioElement.HAVE_NOTHING ||
        !Number.isFinite(audioElement.currentTime)
      ) {
        return;
      }

      state.timeElapsed = Math.min(
        Math.max(0, audioElement.currentTime),
        state.duration ?? Number.MAX_VALUE,
      );
    },
    progress: () => {
      const { buffered, duration } = audioElement;

      if (!configuredSourceUrl || !Number.isFinite(duration) || duration <= 0) {
        return;
      }

      const playhead = Number.isFinite(audioElement.currentTime)
        ? audioElement.currentTime
        : (state.timeElapsed ?? 0);
      const bufferedEnd = Array.from(
        { length: buffered.length },
        (_, index) => ({
          start: buffered.start(index),
          end: buffered.end(index),
        }),
      ).find(
        (bufferedRange) =>
          bufferedRange.start <= playhead && playhead <= bufferedRange.end,
      )?.end;

      state.buffered =
        bufferedEnd !== undefined && Number.isFinite(bufferedEnd)
          ? Math.min(100, Math.max(0, (bufferedEnd / duration) * 100))
          : undefined;
    },
    ended: () => {
      if (!configuredSourceUrl || !isPlaybackRequested.value) {
        return;
      }

      if (state.duration !== undefined) {
        state.timeElapsed = state.duration;
      }

      state.ended = true;
      state.status = "paused";
      onEnded();
    },
    error: () => {
      if (!configuredSourceUrl) {
        return;
      }

      if (!isPlaybackRequested.value && state.status !== "error") {
        clearSource();
        state.status = "paused";
        return;
      }

      reportError(toError(audioElement.error, "Audio playback failed"));
    },
  } satisfies Partial<Record<keyof GlobalEventHandlersEventMap, () => void>>;

  Object.entries(eventListeners).forEach(([eventName, handler]) => {
    audioElement.addEventListener(eventName, handler, {
      passive: true,
      signal,
    });
  });

  function reset() {
    clearSource();
    state.timeElapsed = undefined;
    state.status = "idle";
  }

  signal.addEventListener("abort", reset, { once: true });

  function load(
    sourceUrl: string,
    { startAtSeconds = 0, autoplay = false } = {},
  ) {
    const initialPosition = Number.isFinite(startAtSeconds)
      ? Math.max(0, startAtSeconds)
      : 0;

    clearSource();
    state.status = "paused";
    state.timeElapsed = initialPosition;

    if (autoplay) {
      void startPlayback(sourceUrl, "autoplay");
    } else {
      try {
        loadSource(sourceUrl);
      } catch {
        // Metadata preload is best-effort. Playback retries with a fresh URL.
      }
    }
  }

  function unload() {
    reset();
  }

  async function startPlayback(sourceUrl: string, intent: PlaybackIntent) {
    const reloadSource = state.status === "error";

    if (reloadSource) {
      clearSource();
    }

    state.status = "loading";

    try {
      loadSource(sourceUrl);
    } catch (cause) {
      reportError(
        cause instanceof Error
          ? cause
          : toError(cause, "Audio source loading failed"),
      );
      return;
    }

    try {
      onBeforePlay();

      if (!isPlaybackRequested.value) {
        return;
      }

      await audioElement.play();
    } catch (cause) {
      if (
        !isPlaybackRequested.value ||
        (cause instanceof DOMException && cause.name === "AbortError")
      ) {
        return;
      }

      if (cause instanceof DOMException && cause.name === "NotAllowedError") {
        if (intent === "autoplay") {
          state.status = "paused";
          return;
        }

        reportError(toError(cause, "Playback permission denied"));
        return;
      }

      reportError(toError(cause, "Playback failed"));
    }
  }

  function play(sourceUrl: string) {
    return startPlayback(sourceUrl, "explicit");
  }

  function pause() {
    if (state.status !== "error") {
      state.status = state.status === "idle" ? "idle" : "paused";
    }

    audioElement.pause();
  }

  function seekTo(seconds: number) {
    if (state.timeElapsed === undefined || !Number.isFinite(seconds)) {
      return;
    }

    const target = Math.min(
      Math.max(0, seconds),
      state.duration ?? Number.MAX_VALUE,
    );

    state.ended = false;
    state.timeElapsed = target;

    if (configuredSourceUrl) {
      audioElement.currentTime = target;
    }
  }

  function seekBy(seconds: number) {
    seekTo((state.timeElapsed ?? 0) + seconds);
  }

  return {
    ...toRefs(state),
    isPlaybackRequested,
    position,
    load,
    unload,
    play,
    pause,
    seekTo,
    seekBy,
  };
}
